import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/AppLayout";
import { api } from "@/lib/api";
import { Check, Clock, Search, X, ChevronRight } from "lucide-react";
import InstallAppHint from "@/components/InstallAppHint";
import DateNav, { todayISO } from "@/components/DateNav";

/** Compute the weekday index (0=Mon ... 6=Sun) for a given YYYY-MM-DD string. */
function weekdayFromISO(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  const js = new Date(y, m - 1, d).getDay(); // 0=Sun ... 6=Sat
  return js === 0 ? 6 : js - 1;              // 0=Mon ... 6=Sun
}

/**
 * Mobile-first attendance check-in for coaches on the mats.
 * - Pick date (defaults to today) — supports make-up classes on rest days & backfilling
 * - Pick class from that weekday (horizontal scroll selector)
 * - Tap student row → cycles "in" → "late" → "out" → unset
 * - Optimistic UI; POST to /attendance/mark
 * - Live counts (in/late/total) header chip
 * - Search bar to find a student fast in a 180-student roster
 */
export default function CoachCheckin() {
  const [date, setDate] = useState(todayISO());
  const [classes, setClasses] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [data, setData] = useState({ klass: null, roster: [], counts: { in: 0, late: 0, total: 0 } });
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState({});

  // Load classes for the picked date's weekday. Uses the weekly-schedule endpoint
  // filtered client-side so any day (past or future) is reachable, not just today.
  useEffect(() => {
    let cancelled = false;
    api.get("/classes", { params: { date } }).then(r => {
      if (cancelled) return;
      const dow = weekdayFromISO(date);
      const list = (r.data || [])
        .filter(c => c.day_of_week === dow)
        .sort((a, b) => a.start_time.localeCompare(b.start_time));
      setClasses(list);
      setSelectedId(prev => {
        // Preserve current selection if still valid, otherwise pick first class
        if (prev && list.some(c => c.class_id === prev)) return prev;
        return list[0]?.class_id || null;
      });
    });
    return () => { cancelled = true; };
  }, [date]);

  // Load roster + counts whenever selected class or date changes. Track which class the
  // current state belongs to so taps during a refresh don't mutate the wrong class.
  useEffect(() => {
    if (!selectedId) {
      setData({ klass: null, roster: [], counts: { in: 0, late: 0, total: 0 } });
      return;
    }
    let cancelled = false;
    api.get(`/attendance/roster?class_id=${selectedId}&date=${date}`).then(r => {
      if (!cancelled) setData({ ...r.data, _for: selectedId });
    });
    return () => { cancelled = true; };
  }, [selectedId, date]);

  const filtered = useMemo(() => {
    if (!query.trim()) return data.roster;
    const q = query.toLowerCase();
    return data.roster.filter(s => s.name.toLowerCase().includes(q));
  }, [data.roster, query]);

  // Cycle: null → in → late → out → null
  const cycleStatus = (current) => {
    const order = [null, "in", "late", "out"];
    const next = order[(order.indexOf(current) + 1) % order.length];
    return next;
  };

  const markStudent = async (student) => {
    // Guard: ignore taps while loading OR while the displayed roster belongs to another class
    if (!data.klass || data._for !== selectedId || saving[student.student_id]) return;
    const newStatus = cycleStatus(student.status);
    // Optimistic update
    setData(prev => {
      const newRoster = prev.roster.map(s => s.student_id === student.student_id ? { ...s, status: newStatus } : s);
      const counts = {
        in: newRoster.filter(r => r.status === "in").length,
        late: newRoster.filter(r => r.status === "late").length,
        out: newRoster.filter(r => r.status === "out").length,
        total: newRoster.length,
      };
      return { ...prev, roster: newRoster, counts };
    });
    setSaving(s => ({ ...s, [student.student_id]: true }));
    try {
      // Backend only supports in/late/out — when newStatus is null we send "out" (treated as removal)
      await api.post("/attendance/mark", {
        student_id: student.student_id,
        class_id: selectedId,
        status: newStatus || "out",
        date,
      });
    } finally {
      setSaving(s => { const c = { ...s }; delete c[student.student_id]; return c; });
    }
  };

  if (classes.length === 0) {
    return (
      <div data-testid="page-checkin">
        <PageHeader section="CHECK-IN · MOBILE" title="No classes"
          right={<DateNav date={date} onChange={setDate} testid="checkin-datenav" />}
        />
        <div className="p-6 label" style={{ color: "var(--text-dim)" }} data-testid="checkin-empty">
          Nothing scheduled for this day. Pick another date, or set up your weekly schedule in Classes.
        </div>
      </div>
    );
  }

  const klass = data.klass;
  const checkedPct = data.counts.total ? Math.round((data.counts.in / data.counts.total) * 100) : 0;

  return (
    <div data-testid="page-checkin">
      <PageHeader
        section="CHECK-IN · MOBILE"
        title={klass ? klass.name : "Loading…"}
        right={
          <div className="flex items-center gap-3 flex-wrap justify-end">
            <div className="flex items-center gap-3 mono text-[13px]" data-testid="checkin-counts">
              <span style={{ color: "var(--green)" }}>{data.counts.in} in</span>
              {data.counts.late > 0 && <span style={{ color: "rgb(255,193,7)" }}>{data.counts.late} late</span>}
              <span style={{ color: "var(--text-dim)" }}>of {data.counts.total}</span>
            </div>
            <DateNav date={date} onChange={setDate} testid="checkin-datenav" />
          </div>
        }
      />

      <div className="p-4 md:p-6 space-y-4 max-w-2xl">
        {/* Today's class selector */}
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0" data-testid="checkin-class-tabs">
          {classes.map(c => {
            const active = c.class_id === selectedId;
            return (
              <button
                key={c.class_id}
                onClick={() => setSelectedId(c.class_id)}
                data-testid={`checkin-tab-${c.class_id}`}
                style={{
                  whiteSpace: "nowrap",
                  padding: "10px 14px",
                  borderRadius: 10,
                  border: `1px solid ${active ? "var(--green)" : "var(--border)"}`,
                  background: active ? "rgba(61,220,126,0.1)" : "var(--bg-card)",
                  color: active ? "var(--green)" : "var(--text)",
                  fontSize: 13,
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                <span className="mono" style={{ marginRight: 6, fontSize: 12 }}>{c.start_time}</span>
                {c.name}
              </button>
            );
          })}
        </div>

        {/* Progress bar */}
        <div className="card p-4" data-testid="checkin-progress">
          <div className="flex items-center justify-between mb-2">
            <div className="label">Class roster</div>
            <div className="mono text-[13px]" style={{ color: "var(--green)" }}>{checkedPct}%</div>
          </div>
          <div style={{ height: 6, background: "var(--border)", borderRadius: 999, overflow: "hidden" }}>
            <div style={{
              width: `${checkedPct}%`, height: "100%",
              background: "var(--green)", transition: "width 0.3s ease",
            }} />
          </div>
        </div>

        {/* Search */}
        <div style={{ position: "relative" }}>
          <Search size={16} style={{ position: "absolute", left: 14, top: 14, color: "var(--text-dim)" }} />
          <input
            type="text"
            placeholder="Search students…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="checkin-search"
            style={{
              width: "100%", padding: "12px 14px 12px 40px",
              background: "var(--bg-card)", border: "1px solid var(--border)",
              borderRadius: 10, color: "var(--text)", fontSize: 14,
            }}
          />
        </div>

        {/* Roster */}
        <div className="card" data-testid="checkin-roster">
          {filtered.length === 0 && (
            <div className="p-6 label text-center" style={{ color: "var(--text-dim)" }}>No students match.</div>
          )}
          {filtered.map((s) => {
            const isIn = s.status === "in";
            const isLate = s.status === "late";
            const isOut = s.status === "out";
            const isSaving = saving[s.student_id];
            return (
              <button
                key={s.student_id}
                onClick={() => markStudent(s)}
                disabled={isSaving}
                data-testid={`checkin-row-${s.student_id}`}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "14px 16px",
                  borderBottom: "1px solid var(--border)",
                  background: isIn ? "rgba(61,220,126,0.06)" : "transparent",
                  textAlign: "left",
                  cursor: "pointer",
                  opacity: isSaving ? 0.5 : 1,
                }}
              >
                <div className="logo-mark" style={{
                  background: "var(--bg-card)", color: "var(--text)",
                  border: "1px solid var(--border)", fontSize: 12, flexShrink: 0,
                }}>
                  {s.initials}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="font-semibold text-[14px] truncate">{s.name}</div>
                  <div className="label-tiny mt-0.5" style={{ textTransform: "uppercase" }}>
                    {s.belt} {s.stripes ? `· ${s.stripes} stripe${s.stripes > 1 ? "s" : ""}` : ""}
                  </div>
                </div>
                <StatusBadge isIn={isIn} isLate={isLate} isOut={isOut} />
              </button>
            );
          })}
        </div>

        <div className="label-tiny text-center" style={{ color: "var(--text-dim)" }}>
          Tap a student to cycle: <span style={{ color: "var(--green)" }}>In</span> → <span>Late</span> → <span>Out</span> → unset
        </div>

        <div className="pt-2">
          <InstallAppHint />
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ isIn, isLate, isOut }) {
  if (isIn) return (
    <div style={{
      width: 36, height: 36, borderRadius: "50%",
      background: "var(--green)", color: "var(--bg)",
      display: "grid", placeItems: "center", flexShrink: 0,
    }}>
      <Check size={18} strokeWidth={3} />
    </div>
  );
  if (isLate) return (
    <div style={{
      width: 36, height: 36, borderRadius: "50%",
      background: "rgba(255,193,7,0.15)", color: "rgb(255,193,7)",
      border: "1px solid rgba(255,193,7,0.4)",
      display: "grid", placeItems: "center", flexShrink: 0,
    }}>
      <Clock size={16} />
    </div>
  );
  if (isOut) return (
    <div style={{
      width: 36, height: 36, borderRadius: "50%",
      background: "var(--bg-card)", color: "var(--text-dim)",
      border: "1px solid var(--border)",
      display: "grid", placeItems: "center", flexShrink: 0,
    }}>
      <X size={16} />
    </div>
  );
  return (
    <div style={{
      width: 36, height: 36, borderRadius: "50%",
      border: "1.5px dashed var(--border-strong)",
      display: "grid", placeItems: "center", flexShrink: 0,
      color: "var(--text-dim)",
    }}>
      <ChevronRight size={16} />
    </div>
  );
}
